import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { errorCode, errorMessage } from '@/api/errors';
import { LostPasswordScreen } from '@/auth/LostPasswordScreen';
import { plainText } from '@/auth/plainText';
import { ResetPasswordScreen } from '@/auth/ResetPasswordScreen';
import { asInternalHref } from '@/auth/redirectHref';
import { AlertBox } from '@/common/AlertBox';
import { WBSFullWidthButton } from '@/common/WBSButton';
import { useLogin } from '~/hooks/auth/useLogin';
import {
  AuthCheckbox,
  AuthContainer,
  AuthField,
  AuthHeading,
  AuthHeadingLink,
  AuthHeadingText,
  AuthLabel,
  AuthLink,
  AuthLogoLink,
} from '~/styles/authStyles';
import { asText } from '~/utils/searchParams';
import { formatString } from '~/utils/stringFormat';

interface LoginSearch {
  action?: 'lostpassword' | 'rp';
  key?: string;
  login?: string;
  reset?: 'done';
  /**
   * Where to go after signing in. Set by `useAuthCheck` when it bounces an
   * anonymous visitor off an authenticated route.
   */
  redirect?: string;
}

export const Route = createFileRoute('/_auth/login')({
  component: RouteComponent,
  /**
   * `/login/` switches on `?action=`, as legacy did: Log In, Lost Password and
   * Reset Password share one URL, and existing reset emails link here.
   */
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    // WordPress reached the reset screen as both `rp` and `resetpass`, so both
    // land there rather than on Log In.
    const action =
      search.action === 'lostpassword'
        ? 'lostpassword'
        : search.action === 'rp' || search.action === 'resetpass'
          ? 'rp'
          : undefined;

    return {
      action,
      // Only meaningful on the reset screen, which lifts them into state and
      // strips them from the URL on its first render.
      key: action === 'rp' ? asText(search.key) : undefined,
      login: action === 'rp' ? asText(search.login) : undefined,
      reset: search.reset === 'done' ? 'done' : undefined,
      redirect: asInternalHref(search.redirect),
    };
  },
});

function RouteComponent() {
  const { login, isPending } = useLogin();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/login' });
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (search.action === 'lostpassword') {
    return <LostPasswordScreen />;
  }

  if (search.action === 'rp') {
    return <ResetPasswordScreen />;
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get('email') ?? '');
    const password = String(formData.get('password') ?? '');

    login(email, password, rememberMe)
      .then(() => {
        // `href`, not `to`: the stored value is a pathname plus query (e.g.
        // `/members/123/settings/?verify_email_change=KEY`), and `to` would put
        // the query inside the path. `validateSearch` has already rejected
        // anything off-origin.
        if (search.redirect !== undefined) {
          navigate({ href: search.redirect, replace: true });
        } else {
          navigate({ to: '/' });
        }
      })
      .catch((failure: unknown) => {
        switch (errorCode(failure)) {
          case 'password_reset_required':
            // The account can't be used until its password is set, so send them
            // to that page with the address they typed.
            navigate({
              to: '/first-time-password-reset/',
              search: { email },
            });
            return;
          case 'resource_not_found':
            setError(plainText(t('notifications.error-there-is-no-2')));
            return;
          case 'forbidden':
            setError(
              formatString(
                plainText(t('notifications.error-the-password-you')),
                email,
              ),
            );
            return;
          default:
            setError(
              errorMessage(failure, t('wbs.something-went-wrong-please')),
            );
        }
      });
  };

  return (
    <AuthContainer>
      <AuthLogoLink to="/">
        {/* Self-hosted; legacy hotlinked the WordPress uploads directory. */}
        <img
          alt={t('wbs.world-bible-school')}
          src="/images/stack-logo-en-200w.png"
        />
      </AuthLogoLink>
      <AuthHeading>
        {/* Translation Needed */}
        <AuthHeadingText>Log In</AuthHeadingText>
        <AuthHeadingLink to="/register">
          {t('actions.create-an-account')}
        </AuthHeadingLink>
      </AuthHeading>
      {/* The post-reset notice. Legacy used a one-time server-side flag; a
         search param survives a reload instead. */}
      {search.reset === 'done' && (
        <AlertBox variant="success">
          {t('wbs.your-password-has-been-reset')}
        </AlertBox>
      )}

      {error !== null && <AlertBox variant="error">{error}</AlertBox>}

      <form onSubmit={handleSubmit}>
        <AuthLabel htmlFor="email">
          {t('notifications.email-address')}
        </AuthLabel>
        <AuthField id="email" name="email" type="email" fullWidth />

        <AuthLabel htmlFor="password">{t('wbs.password')}</AuthLabel>
        <PasswordWrapper>
          <AuthField
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
          />
          {/* Translation Needed */}
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label="Toggle password visibility"
          >
            {showPassword ? (
              <FontAwesomeIcon icon={faEyeSlash} />
            ) : (
              <FontAwesomeIcon icon={faEye} />
            )}
          </PasswordToggle>
        </PasswordWrapper>

        <RememberMeWrapper>
          <RememberMeLabel htmlFor="rememberme">
            <AuthCheckbox
              id="rememberme"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>{t('profile.remember-me')}</span>
          </RememberMeLabel>
        </RememberMeWrapper>

        {/* Translation Needed */}
        <WBSFullWidthButton type="submit" wide isLoading={isPending}>
          Log In
        </WBSFullWidthButton>
      </form>
      <FooterLinks>
        <AuthLink to="/privacy">{t('wbs.privacy-policy')}</AuthLink>
        <LinkSeparator>|</LinkSeparator>
        <AuthLinkWrapper>
          <Link
            to="/login/"
            search={{
              action: 'lostpassword',
            }}
          >
            {t('wbs.forgot-password')}
          </Link>
        </AuthLinkWrapper>
      </FooterLinks>
    </AuthContainer>
  );
}

const AuthLinkWrapper = styled.span`
  a {
    color: var(--wbs-red);
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
  }
`;

const PasswordWrapper = styled.div`
  margin-bottom: 20px;
  position: relative;
  width: 100%;

  ${AuthField} {
    margin-bottom: 0;
    padding-right: 40px;
    width: 100%;
  }
`;

const PasswordToggle = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: var(--black);
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  &:hover {
    color: var(--grey-800);
  }

  &:focus {
    outline: 2px solid var(--wbs-red);
    outline-offset: 2px;
  }
`;

const RememberMeWrapper = styled.div`
  margin-bottom: 20px;
`;

const RememberMeLabel = styled.label`
  align-items: center;
  color: var(--black);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  line-height: normal;

  @media (max-width: 768px) {
    align-items: flex-start;
    display: flex;
  }
`;

const FooterLinks = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  font-size: 13px;
  gap: 10px;
  justify-content: center;
  margin-top: 18px;
  width: 100%;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 6px 10px;
    justify-content: center;
  }

  > * {
    align-items: center;
    display: inline-flex;
  }
`;

const LinkSeparator = styled.span`
  color: var(--grey-450);
  font-size: 16px;
  font-weight: 300;
`;
